'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { PortfolioSectionNav } from './components/PortfolioScaffold';
import PhotographsSection from './components/PhotographsSection';
import GraphicsSection from './components/GraphicsSection';
import ThreeDSection from './components/ThreeDSection';
import MonolythNewNatureSection from './components/MonolythNewNatureSection';
import MonolythVRSection from './components/MonolythVRSection';
import ProcessGlyphsSection from './components/ProcessGlyphsSection';
import CulturalCenterSection from './components/CulturalCenterSection';
import SerenaWilliamsSection from './components/SerenaWilliamsSection';

// ─── EXACT CONTENT FROM BEHANCE GALLERY ──────────────────────────────────────────────────
// https://www.behance.net/gallery/112291665/Monolyth-(VR)
// All text, images, iframes extracted directly from the live page

const TIMELINE_EVENTS = [
{
  year: '2018',
  phase: 'Origin',
  monolyth: 'Monolyth concept emerges — multi-layered VR environments exploring three-reality system: physical, digital, spiritual. Pittsburgh/Braddock environmental research begins.',
  haalBridge: 'The three-layer reality system maps directly to Zahir (manifest), Batin (hidden), and Ghayb (unseen) — the same framework underlying the IRL Lens.',
  pillar: 'IRL',
  pillarColor: '#8a8ab8'
},
{
  year: '2019',
  phase: 'Research',
  monolyth: 'Detroit community engagement — documenting urban ecosystems, cultural centers, and community spaces through 360° panoramic photography. Glyph system development begins.',
  haalBridge: 'Community documentation practice becomes the foundation for the Muslim Resource Guide\'s ward-mapping methodology and the Ecosystem Journey observation system.',
  pillar: 'MRG',
  pillarColor: '#2d7a47'
},
{
  year: '2020',
  phase: 'Expansion',
  monolyth: 'Chicago research phase — South Side and West Side community spaces. Cultural Center concept developed. Diawara collaboration. Archive section compiled.',
  haalBridge: 'Chicago community research directly informs the Asbab Quest challenge framework — CIOGC, MAS, MANA partnerships rooted in this geographic and cultural grounding.',
  pillar: 'MRG',
  pillarColor: '#2d7a47'
},
{
  year: '2021',
  phase: 'Synthesis',
  monolyth: 'Monolyth VR published on Behance. Visual language of glyphs, base images, and process documentation crystallized. The "open world" concept fully articulated.',
  haalBridge: 'The open-world concept becomes Ayat in Creation — the game that will tie together all Haal Project components. Glyph system evolves into the Names of Allah tile library.',
  pillar: 'Ayat',
  pillarColor: '#2d8a87'
},
{
  year: '2022',
  phase: 'Transition',
  monolyth: 'Coming into deen — the surreal experiences described in Section 41 of the Open Letter begin. The Quran becomes the study guide. Monolyth\'s creative vision finds its source.',
  haalBridge: 'The transition from Monolyth to Haal Project — creative principles find their root in submission to Al-Khaliq. The platform is not the vision; it is a means toward recognition.',
  pillar: 'Keys',
  pillarColor: '#D4A82A'
},
{
  year: '2023',
  phase: 'Foundation',
  monolyth: 'Open Letter: Deep Green begins — 41 sections documenting the journey from Monolyth\'s creative principles to the Haal Project\'s caretaking framework.',
  haalBridge: 'Keys to Kulli Haal curriculum developed. The four pillars emerge: Keys (curriculum), Ayat (open world), IRL Lens (recognition), MRG (community utility).',
  pillar: 'Keys',
  pillarColor: '#D4A82A'
},
{
  year: '2024',
  phase: 'Build',
  monolyth: 'Haal Project platform development begins. Monolyth\'s visual language — dark environments, gold accents, layered depth — becomes the design language of the platform.',
  haalBridge: 'The IRL Lens launches — Monolyth\'s three-reality system becomes an interactive tool for recognizing Ayat in creation. The garden system mirrors the open-world concept.',
  pillar: 'IRL',
  pillarColor: '#8a8ab8'
},
{
  year: '2025',
  phase: 'Integration',
  monolyth: 'Muslim Resource Guide beta — community mapping tools informed by Pittsburgh, Detroit, and Chicago research. Ecosystem Journey observation system launched.',
  haalBridge: 'Asbab Quest feed connects verified Islamic organizations to caretaking challenges. The community documentation practice from Monolyth becomes a live, real-time system.',
  pillar: 'MRG',
  pillarColor: '#2d7a47'
},
{
  year: '2026',
  phase: 'Convergence',
  monolyth: 'Ayat in Creation game enters pre-production. Monolyth\'s VR environments become the visual reference for the game\'s open-world ecosystem design.',
  haalBridge: 'All four pillars converge: Keys (curriculum) → Ayat (open world) → IRL Lens (recognition) → MRG (community action). The Haal Project is the Monolyth, fully unfolded.',
  pillar: 'Ayat',
  pillarColor: '#2d8a87'
}];

const PILLAR_COMPARISONS = [
{
  monolyth: 'Three-Layer Reality System',
  monolythDesc: 'Physical environment, digital overlay, spiritual dimension — three simultaneous realities explored through VR.',
  haal: 'Inner Reality Lens (IRL)',
  haalDesc: 'Zahir (manifest), Batin (hidden), Ghayb (unseen) — three tracks of Ayat recognition: within, around, revealed.',
  pillar: 'IRL',
  color: '#8a8ab8',
  icon: '◎'
},
{
  monolyth: 'Glyph System',
  monolythDesc: 'Visual language of symbols encoding environmental and cultural meaning across Pittsburgh, Detroit, and Chicago.',
  haal: 'Keys to Kulli Haal',
  haalDesc: 'Arabic root system as a curriculum — each root a module, each Name a key to a different function of the garden.',
  pillar: 'Keys',
  color: '#D4A82A',
  icon: '⌘'
},
{
  monolyth: 'Open World Environments',
  monolythDesc: '360° panoramic environments — immersive, explorable, layered with ecological and cultural data.',
  haal: 'Ayat in Creation',
  haalDesc: 'Open-world journey through local ecosystems — identify species, document reflections, link to Divine Names.',
  pillar: 'Ayat',
  color: '#2d8a87',
  icon: '✦'
},
{
  monolyth: 'Community Documentation',
  monolythDesc: 'Field research in Pittsburgh, Detroit, Chicago — mapping community spaces, cultural centers, and urban ecosystems.',
  haal: 'Muslim Resource Guide',
  haalDesc: 'Ward-mapping tool connecting community needs to caretaking resources — the documentation practice made actionable.',
  pillar: 'MRG',
  color: '#2d7a47',
  icon: '⬡'
}];

function MonolythHaalTimeline() {
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const [view, setView] = useState<'timeline' | 'comparison'>('timeline');

  return (
    <div style={{ background: '#060d12', padding: '80px 0', borderTop: '1px solid rgba(201,168,76,0.12)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 32px' }}>

        {/* Section header */}
        <div style={{ marginBottom: 48 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>
            ◼ Creative Evolution · 2018–2026
          </p>
          <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 36, color: '#F5F0E8', fontWeight: 400, marginBottom: 12, lineHeight: 1.2 }}>
            Monolyth → The Haal Project
          </h2>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: 'rgba(212,197,169,0.65)', lineHeight: 1.8, maxWidth: 700, marginBottom: 24 }}>
            The creative principles cultivated in Monolyth (2018–2021) — three-layer reality systems, glyph languages, open-world environments, and community documentation — are the seeds from which the Haal Project grew. This timeline traces the evolution from pre-deen creative vision to the four pillars of caretaking.
          </p>

          {/* View toggle */}
          <div style={{ display: 'flex', gap: 8 }}>
            {(['timeline', 'comparison'] as const).map((v) =>
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                fontFamily: 'monospace',
                fontSize: 9,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                padding: '6px 14px',
                borderRadius: 3,
                cursor: 'pointer',
                border: `1px solid ${view === v ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.15)'}`,
                background: view === v ? 'rgba(201,168,76,0.1)' : 'transparent',
                color: view === v ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                transition: 'all 0.15s'
              }}>
              {v === 'timeline' ? '◈ Timeline' : '⬡ Side-by-Side'}
            </button>
            )}
          </div>
        </div>

        {/* TIMELINE VIEW */}
        {view === 'timeline' &&
        <div style={{ position: 'relative' }}>
            {/* Vertical line */}
            <div style={{
            position: 'absolute',
            left: 60,
            top: 0,
            bottom: 0,
            width: 1,
            background: 'linear-gradient(to bottom, rgba(201,168,76,0.3), rgba(201,168,76,0.05))'
          }} />

            {TIMELINE_EVENTS.map((event) => {
            const isActive = activeYear === event.year;
            return (
              <div
                key={event.year}
                style={{ display: 'flex', gap: 32, marginBottom: 32, cursor: 'pointer' }}
                onClick={() => setActiveYear(isActive ? null : event.year)}>

                  {/* Year + dot */}
                  <div style={{ width: 60, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                    <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: isActive ? event.pillarColor : 'rgba(201,168,76,0.3)',
                    border: `2px solid ${isActive ? event.pillarColor : 'rgba(201,168,76,0.2)'}`,
                    boxShadow: isActive ? `0 0 12px ${event.pillarColor}60` : 'none',
                    transition: 'all 0.2s',
                    zIndex: 1,
                    marginBottom: 4
                  }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 10, color: isActive ? '#c9a84c' : 'rgba(201,168,76,0.5)', letterSpacing: '.06em', writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                      {event.year}
                    </span>
                  </div>

                  {/* Content */}
                  <div style={{
                  flex: 1,
                  background: isActive ? 'rgba(6,13,18,0.95)' : 'rgba(6,13,18,0.6)',
                  border: `1px solid ${isActive ? `${event.pillarColor}33` : 'rgba(201,168,76,0.08)'}`,
                  borderLeft: `3px solid ${event.pillarColor}`,
                  borderRadius: 6,
                  padding: '14px 18px',
                  transition: 'all 0.2s'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: event.pillarColor }}>
                        {event.phase}
                      </span>
                      <span style={{
                      fontFamily: 'monospace',
                      fontSize: 7,
                      letterSpacing: '.1em',
                      textTransform: 'uppercase',
                      color: event.pillarColor,
                      background: `${event.pillarColor}15`,
                      border: `1px solid ${event.pillarColor}30`,
                      borderRadius: 3,
                      padding: '2px 6px'
                    }}>
                        {event.pillar}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 6 }}>
                          ◼ Monolyth
                        </div>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, margin: 0 }}>
                          {event.monolyth}
                        </p>
                      </div>
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${event.pillarColor}99`, marginBottom: 6 }}>
                          ✦ Haal Project
                        </div>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7, margin: 0 }}>
                          {event.haalBridge}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>);
          })}
          </div>
        }

        {/* SIDE-BY-SIDE COMPARISON VIEW */}
        {view === 'comparison' &&
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 32 }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '20px 24px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.4)', marginBottom: 8 }}>
                  ◼ 2018–2021
                </div>
                <h3 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, marginBottom: 8 }}>
                  Monolyth (VR)
                </h3>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, margin: 0 }}>
                  Pre-deen creative archive. Environmental research, community documentation, and open-world VR design across Pittsburgh, Detroit, and Chicago.
                </p>
              </div>

              <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: '20px 24px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>
                  ✦ 2022–2026
                </div>
                <h3 style={{ fontFamily: "'Amiri', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 400, marginBottom: 8 }}>
                  The Haal Project
                </h3>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.7, margin: 0 }}>
                  Non-profit caretaking platform. Four pillars: Keys to Kulli Haal, Ayat in Creation, Inner Reality Lens, Muslim Resource Guide.
                </p>
              </div>
            </div>

            {PILLAR_COMPARISONS.map((comp) =>
          <div key={comp.pillar} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 0, marginBottom: 16, alignItems: 'stretch' }}>
                <div style={{
              background: 'rgba(6,13,18,0.7)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              padding: '16px 20px'
            }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.5)', marginBottom: 6 }}>
                    ◼ {comp.monolyth}
                  </div>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7, margin: 0 }}>
                    {comp.monolythDesc}
                  </p>
                </div>

                <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              background: `${comp.color}15`,
              border: `1px solid ${comp.color}30`,
              borderLeft: `3px solid ${comp.color}`,
              borderRight: `3px solid ${comp.color}`,
              flexShrink: 0
            }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 14, color: comp.color }}>{comp.icon}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: comp.color, letterSpacing: '.08em', writingMode: 'vertical-rl' }}>
                      {comp.pillar}
                    </span>
                    <span style={{ fontSize: 10, color: `${comp.color}88` }}>→</span>
                  </div>
                </div>

                <div style={{
              background: `${comp.color}08`,
              border: `1px solid ${comp.color}20`,
              borderLeft: 'none',
              borderRadius: '0 6px 6px 0',
              padding: '16px 20px'
            }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: comp.color, marginBottom: 6 }}>
                    ✦ {comp.haal}
                  </div>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.65)', lineHeight: 1.7, margin: 0 }}>
                    {comp.haalDesc}
                  </p>
                </div>
              </div>
          )}

            <div style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 8,
            padding: '24px 28px',
            marginTop: 32
          }}>
              <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 12 }}>
                ◈ Evolution Narrative
              </div>
              <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,197,169,0.75)', lineHeight: 1.9, margin: 0 }}>
                &ldquo;I started writing it when I first came into deen to try and express the very surreal occurrences that I was experiencing. It was difficult to explain them to people, muslims or otherwise, &apos;til I spent time immersed in deen-related study to find thoughts and terms, Mata (tools), that when pieced together, expressed analogies that helped present some way of elucidating what seemed to be on the other side of a thinly veiled curtain of reality pulled back in very scary episodes.&rdquo;
              </p>
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.08em', marginTop: 12, marginBottom: 0 }}>
                — Section 41: The Haal Project, Open Letter: Deep Green
              </p>
            </div>
          </div>
        }

        <div style={{ marginTop: 40, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="/open-letter" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#c9a84c', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 4, padding: '8px 16px', textDecoration: 'none' }}>
            ✦ Read the Open Letter
          </a>
          <a href="/ayat-in-creation" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#2d8a87', background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.25)', borderRadius: 4, padding: '8px 16px', textDecoration: 'none' }}>
            ◎ Ayat in Creation
          </a>
          <a href="/keys-to-kulli-haal" style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#D4A82A', background: 'rgba(212,168,42,0.08)', border: '1px solid rgba(212,168,42,0.25)', borderRadius: 4, padding: '8px 16px', textDecoration: 'none' }}>
            ⌘ Keys to Kulli Haal
          </a>
        </div>
      </div>
    </div>);
}

export default function MonolythPage() {
  const [activeTab, setActiveTab] = useState<'project' | 'about' | 'evolution'>('project');

  return (
    <div style={{ fontFamily: "'acumin-pro', 'Helvetica Neue', Helvetica, Arial, sans-serif", backgroundColor: '#060d12', minHeight: '100vh' }}>
      {/* Haal Project Header */}
      <Header />

      {/* ── BREADCRUMB CONTEXT BANNER ── */}
      <div style={{
        background: 'linear-gradient(90deg, rgba(45,138,135,0.08) 0%, rgba(212,168,42,0.06) 100%)',
        borderBottom: '1px solid rgba(45,138,135,0.15)',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
      }}>
        <Link href="/" style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)', textDecoration: 'none', letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Haal Project
        </Link>
        <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10 }}>›</span>
        <Link href="/portfolio/monolyth" style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(201,168,76,0.7)', textDecoration: 'none', letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Portfolio
        </Link>
        <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10 }}>›</span>
        <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#c9a84c', letterSpacing: '.06em', textTransform: 'uppercase' }}>
          Monolyth (VR)
        </span>
        <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.3)', letterSpacing: '.04em' }}>
          Pre-Haal Archive · 2018–2021 · Environmental Research &amp; VR Design
        </span>
      </div>

      {/* Tab navigation */}
      <div style={{
        backgroundColor: '#060d12',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        padding: '0 24px',
        display: 'flex',
        gap: 0
      }}>
        {(['project', 'about', 'evolution'] as const).map((tab) =>
        <button
          key={tab}
          onClick={() => setActiveTab(tab)}
          style={{
            padding: '14px 20px',
            background: 'none',
            border: 'none',
            borderBottom: `2px solid ${activeTab === tab ? '#c9a84c' : 'transparent'}`,
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: activeTab === tab ? '#c9a84c' : 'rgba(212,197,169,0.4)',
            transition: 'all 0.15s'
          }}>
          {tab === 'project' ? '◈ Project Gallery' : tab === 'about' ? '✦ About' : '⬡ Monolyth → Haal'}
        </button>
        )}
      </div>

      {/* Project Header Bar */}
      <div style={{
        backgroundColor: '#060d12',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 14
      }}>
        <a href="https://www.behance.net/quickfreelances" target="_blank" rel="noopener noreferrer">
          <img
            src="https://pps.services.adobe.com/api/profile/14AC210654EB5E250A4C98A7@AdobeID/image/b9086a83-b44e-4bf2-9b84-1b02b3887180/100"
            alt="Abdullah Quick — Haal Project founder"
            style={{ width: 44, height: 44, borderRadius: '50%', display: 'block', border: '2px solid rgba(201,168,76,0.3)' }} />
        </a>
        <div>
          <h1 style={{ fontFamily: "'Amiri', serif", fontSize: 22, fontWeight: 400, margin: 0, color: '#F5F0E8', letterSpacing: '.02em' }}>Monolyth (VR)</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
            <a href="https://www.behance.net/quickfreelances" target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.6)', textDecoration: 'none', letterSpacing: '.06em' }}>
              Abdullah Quick
            </a>
            <span style={{ color: 'rgba(201,168,76,0.2)' }}>·</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.35)', letterSpacing: '.06em' }}>2018–2021 · Pre-Haal Archive</span>
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 10px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(201,168,76,0.6)', letterSpacing: '.08em', textTransform: 'uppercase' }}>
            VR · Environment Design
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 10px', borderRadius: 20, background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', color: '#2d8a87', letterSpacing: '.08em', textTransform: 'uppercase' }}>
            Community Research
          </span>
        </div>
      </div>

      {/* Main content area */}
      <div style={{ display: 'flex', maxWidth: 1400, margin: '0 auto', position: 'relative' }}>
        <main style={{ flex: 1, minWidth: 0, paddingBottom: 70 }}>

          {/* ── EVOLUTION TAB ── */}
          {activeTab === 'evolution' && <MonolythHaalTimeline />}

          {/* ── ABOUT TAB ── */}
          {activeTab === 'about' &&
          <div style={{ background: '#060d12', padding: '60px 32px' }}>
              <div style={{ maxWidth: 800, margin: '0 auto' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 8 }}>
                  ◼ About This Project
                </p>
                <h2 style={{ fontFamily: "'Amiri', serif", fontSize: 36, color: '#F5F0E8', fontWeight: 400, marginBottom: 20 }}>
                  Monolyth (VR)
                </h2>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: 'rgba(212,197,169,0.75)', lineHeight: 1.9, marginBottom: 24 }}>
                  Monolyth is a multi-stage VR project exploring environmental racism through immersive digital environments. The project documents industrial corridors in Pittsburgh, Detroit, and Chicago — transforming street maps of affected communities into surreal glyphs and open-world environments.
                </p>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: 'rgba(212,197,169,0.75)', lineHeight: 1.9, marginBottom: 24 }}>
                  The platform presents research alongside cultural products from local creatives and small minority-owned businesses, creating a virtual cultural center that reclaims industrial corridors through data-driven streetmap Glyphs as explorable environments.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 32 }}>
                  <a href="https://www.behance.net/gallery/112291665/Monolyth-(VR)" target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: '#c9a84c', background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 4, padding: '8px 16px', textDecoration: 'none' }}>
                    → View on Behance
                  </a>
                </div>
              </div>
            </div>
          }

          {/* ── PROJECT GALLERY TAB ── */}
          {activeTab === 'project' &&
          <div style={{ backgroundColor: '#060d12', paddingTop: 40 }}>

              {/* Intro text */}
              <div style={{ padding: '0 7.1%', marginBottom: 20 }}>
                <div style={{
                textAlign: 'center',
                color: '#696969',
                fontFamily: 'helvetica, arial, sans-serif',
                fontSize: 20,
                fontWeight: 400,
                lineHeight: '1.4em'
              }}>
                  This is a multi-stage journey. These were the previous stages:
                </div>
              </div>

              {/* Photographs link */}
              <div style={{ padding: '0 7.1%', marginBottom: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <a
                  href="https://www.behance.net/gallery/216127137/25-Photographs"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{ textDecoration: 'none' }}>
                    <span style={{
                    color: '#2174c9',
                    fontFamily: 'helvetica, arial, sans-serif',
                    fontSize: 29,
                    fontWeight: 700
                  }}>&nbsp;Photographs&nbsp;</span>
                  </a>
                </div>
              </div>

              {/* Photographs cover image */}
              <div style={{ marginBottom: 20 }}>
                <img
                src="https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/98b88d112291665.67fbabc562369.jpg"
                alt="Monolyth VR — Photographs"
                style={{ width: '100%', display: 'block' }} />
              </div>

              {/* Graphics link */}
              <div style={{ padding: '0 7.1%', marginBottom: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <a
                  href="https://www.behance.net/gallery/216150447/25-Graphics"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{ textDecoration: 'none' }}>
                    <span style={{
                    color: '#287bcf',
                    fontFamily: 'helvetica, arial, sans-serif',
                    fontSize: 30,
                    fontWeight: 700
                  }}>Graphics</span>
                  </a>
                </div>
              </div>

              {/* Graphics cover image */}
              <div style={{ marginBottom: 20 }}>
                <img
                src="https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/2ca22d112291665.67fbabc561e05.jpg"
                alt="Monolyth VR — Graphics"
                style={{ width: '100%', display: 'block' }} />
              </div>

              {/* 3D link */}
              <div style={{ padding: '0 7.1%', marginBottom: 20 }}>
                <div style={{ textAlign: 'center' }}>
                  <a
                  href="https://www.behance.net/gallery/216159031/25-3D"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  style={{ textDecoration: 'none' }}>
                    <span style={{
                    color: '#1f73c9',
                    fontFamily: 'helvetica, arial, sans-serif',
                    fontSize: 30,
                    fontWeight: 700
                  }}>3D</span>
                  </a>
                </div>
              </div>

              {/* 3D cover image */}
              <div style={{ marginBottom: 20 }}>
                <img
                src="https://mir-s3-cdn-cf.behance.net/project_modules/1400_webp/078460112291665.67fbabc562aa1.jpg"
                alt="Monolyth VR — 3D"
                style={{ width: '100%', display: 'block' }} />
              </div>

              {/* ── PORTFOLIO SECTIONS ── */}
              <PortfolioSectionNav />
              <PhotographsSection />
              <GraphicsSection />
              <ThreeDSection />

              {/* ── MONOLYTH: A NEW NATURE ── */}
              <MonolythNewNatureSection />

              {/* ── MONOLYTH VR SECTION ── */}
              <MonolythVRSection />

              {/* ── PROCESS / GLYPHS / BASE IMAGES ── */}
              <ProcessGlyphsSection />

              {/* ── CULTURAL CENTER / DIAWARA / ARCHIVE ── */}
              <CulturalCenterSection />

              {/* ── SERENA WILLIAMS DESIGN CREW 3D + BISMILLAH ── */}
              <SerenaWilliamsSection />

            </div>
          }
        </main>
      </div>

      {/* Haal Project Footer */}
      <Footer />
    </div>);
}